import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  format, 
  addDays, 
  startOfDay, 
  eachDayOfInterval, 
  isSameDay,
  getHours,
  getMinutes
} from 'date-fns';
import { Users, Plus } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { SocialIntent } from '../types/SocialEvent';
import type { SocialEvent } from '../types/SocialEvent';
import { getEventVisualState } from '../logic/visualState';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const ROW_HEIGHT = 64; // 1 hour = 64px
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function ensureDate(time: number | string | Date): Date {
  if (time instanceof Date) return time;
  if (typeof time === 'string') return new Date(time);
  // If it's a number, check if it's seconds (roughly < 10^11) or milliseconds
  if (time < 10000000000) return new Date(time * 1000);
  return new Date(time);
}

interface PresenceGridProps {
  events: SocialEvent[];
  startDate?: Date;
  daysToShow?: number;
  onEventClick?: (event: SocialEvent) => void;
  onJoinEvent?: (event: SocialEvent) => void;
}

const EventCard = ({ 
  event, 
  onClick, 
  onJoin 
}: { 
  event: SocialEvent; 
  onClick?: ((e: SocialEvent) => void) | undefined;
  onJoin?: ((e: SocialEvent) => void) | undefined;
}) => {
  const visualClasses = getEventVisualState(event);
  const start = ensureDate(event.startTime);
  const end = ensureDate(event.endTime);
  
  const top = (getHours(start) + getMinutes(start) / 60) * ROW_HEIGHT;
  const duration = (end.getTime() - start.getTime()) / 1000 / 60 / 60; // hours
  const height = duration * ROW_HEIGHT;

  const isOpenInvite = event.intent === SocialIntent.OPEN_INVITE;

  return (
    <motion.div
      layoutId={event.id}
      onClick={() => onClick?.(event)}
      whileHover={isOpenInvite ? { scale: 1.02, zIndex: 10 } : {}}
      className={cn(
        "absolute left-1 right-1 rounded-lg p-2 cursor-pointer transition-shadow overflow-hidden group",
        visualClasses,
        "shadow-sm hover:shadow-md"
      )}
      style={{ top, height, minHeight: '24px' }}
    >
      <div className="flex flex-col h-full justify-between">
        <div className="flex flex-col">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold truncate leading-tight">{event.title}</span>
            {isOpenInvite && (
               <div className="flex items-center space-x-1 text-[10px] opacity-80">
                 <Users size={10} />
                 <span>{event.currentParticipants.length}</span>
               </div>
            )}
          </div>
          <span className="text-[10px] opacity-70 leading-none">
            {format(start, 'h:mm')} - {format(end, 'h:mm a')}
          </span>
        </div>

        {/* Reveal Join Button on Hover for OPEN_INVITE */}
        <AnimatePresence>
          {isOpenInvite && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mt-auto bg-indigo-600 text-white text-[10px] py-1 px-2 rounded flex items-center justify-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                onJoin?.(event);
              }}
            >
              <Plus size={10} />
              <span>Join</span>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export const PresenceGrid: React.FC<PresenceGridProps> = ({
  events,
  startDate = new Date(),
  daysToShow = 3,
  onEventClick,
  onJoinEvent
}) => {
  const days = useMemo(() => {
    const start = startOfDay(startDate);
    return eachDayOfInterval({
      start,
      end: addDays(start, daysToShow - 1)
    });
  }, [startDate, daysToShow]);

  // Find "Beacon" events to render the Social Layer
  const beacons = events.filter(e => e.intent === SocialIntent.BEACON);

  return (
    <div className="flex flex-col w-full h-full bg-zinc-50 rounded-xl overflow-hidden border border-zinc-200 shadow-xl">
      {/* Grid Header */}
      <div className="flex border-bottom border-zinc-200 bg-white/80 backdrop-blur-md sticky top-0 z-20">
        <div className="w-16 flex-shrink-0" /> {/* Axis spacer */}
        {days.map((day, i) => (
          <div key={i} className="flex-1 py-4 text-center border-l border-zinc-100">
            <span className="block text-[10px] uppercase tracking-wider font-semibold text-zinc-400">
              {format(day, 'EEE')}
            </span>
            <span className="text-lg font-bold text-zinc-800">{format(day, 'd')}</span>
          </div>
        ))}
      </div>

      {/* Grid Content */}
      <div className="flex flex-1 overflow-y-auto relative custom-scrollbar">
        {/* Time Axis */}
        <div className="w-16 flex-shrink-0 bg-white/50 border-r border-zinc-200">
          {HOURS.map(hour => (
            <div key={hour} className="h-[64px] flex items-start justify-center pr-2 pt-1">
              <span className="text-[10px] font-medium text-zinc-400">
                {hour === 0 ? '12 AM' : hour > 12 ? `${hour - 12} PM` : `${hour} ${hour === 12 ? 'PM' : 'AM'}`}
              </span>
            </div>
          ))}
        </div>

        {/* The Presence Columns */}
        <div className="flex-1 flex relative">
          {/* Subtle Grid Lines */}
          <div className="absolute inset-0 pointer-events-none">
            {HOURS.map(hour => (
              <div key={hour} className="h-[64px] border-b border-zinc-100/80 w-full" />
            ))}
          </div>

          {days.map((day, i) => {
            const dayEvents = events.filter(e => isSameDay(ensureDate(e.startTime), day));
            const dayBeacons = beacons.filter(e => isSameDay(ensureDate(e.startTime), day));

            return (
              <div key={i} className="flex-1 relative border-l border-zinc-100 group/column">
                {/* Social Layer (Beacons) */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                  {dayBeacons.map(beacon => {
                    const bStart = ensureDate(beacon.startTime);
                    const bEnd = ensureDate(beacon.endTime);
                    const top = (getHours(bStart) + getMinutes(bStart) / 60) * ROW_HEIGHT;
                    const duration = (bEnd.getTime() - bStart.getTime()) / 1000 / 60 / 60;
                    const height = duration * ROW_HEIGHT;
                    
                    return (
                      <motion.div
                        key={`beacon-${beacon.id}`}
                        initial={{ opacity: 0 }}
                        animate={{ 
                          opacity: [0.1, 0.2, 0.1],
                          scale: [1, 1.05, 1],
                        }}
                        transition={{ 
                          duration: 4, 
                          repeat: Infinity, 
                          ease: "easeInOut" 
                        }}
                        className="absolute inset-x-0 rounded-full blur-3xl bg-gradient-radial from-purple-400/20 to-transparent"
                        style={{ top, height }}
                      />
                    );
                  })}
                </div>

                {/* Event Cards */}
                {dayEvents.filter(e => e.intent !== SocialIntent.BEACON).map(event => (
                  <EventCard 
                    key={event.id} 
                    event={event} 
                    onClick={onEventClick}
                    onJoin={onJoinEvent}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
